<template>
  <div class="custom-ui-checkbox-wrapper">
    <label
      class="custom-ui-checkbox"
      :class="{ checked: isSelected || isSelectedAll, disabled: isDisabled }"
    >
      <input
        name="ui-checkbox"
        type="checkbox"
        :value="value"
        :checked="isSelected"
        :disabled="disabled"
        :aria-label="label || 'checkbox'"
        @input="toggleCheck"
        @focus="focused = true"
        @blur="focused = false"
      />
      <i class="fa-solid fa-check" v-if="isSelected || isSelectedAll" />
    </label>
    <span class="custom-ui-checkbox-label" :class="{ disabled: isDisabled }" v-text="label" />
  </div>
</template>

<script lang="ts" setup>
  import type { ICheckbox } from '@/types/Components'
  import { computed, ref } from 'vue'

  const { label = '', value, disabled = false, isAll = false } = defineProps<ICheckbox>()

  const model = defineModel<any>()

  const focused = ref<boolean>(false)

  const isModelArray = computed(() => {
    return Array.isArray(model.value)
  })

  const isValueObject = computed(() => {
    return value !== null && typeof value === 'object'
  })

  const isSelected = computed(() => {
    if (isModelArray.value) {
      if (isValueObject.value) {
        return model.value.some((obj: any) => isEqual(obj, value))
      } else {
        return model.value.includes(value)
      }
    }

    if (isValueObject.value) {
      return isEqual(model.value, value)
    }

    return model.value === value
  })

  const isSelectedAll = computed(() => {
    if (isAll) {
      return model.value.length === value.length
    } else {
      return false
    }
  })

  const isDisabled = computed(() => {
    return disabled
  })

  const toggleCheck = () => {
    if (!disabled) {
      if (Array.isArray(model.value)) {
        handleArrayCheckbox()
      } else {
        handleSingleSelectCheckbox()
      }
    }
  }

  const handleArrayCheckbox = () => {
    const newModel = model.value

    if (!isSelected.value) {
      addValueToModel(newModel)
    } else {
      removeValueFromModel(newModel)
    }
  }

  const handleSingleSelectCheckbox = () => {
    if (typeof value == 'boolean') {
      model.value = !model.value
    } else {
      model.value = isSelected.value ? '' : value
    }
  }

  const removeValueFromModel = (newModel: any) => {
    if (isValueObject.value) {
      newModel = newModel.filter((obj: any) => !isEqual(obj, value))
    } else {
      const index = newModel.indexOf(value)

      if (index !== -1) {
        newModel.splice(index, 1)
      }
    }
    model.value = newModel
  }

  const addValueToModel = (newModel: any) => {
    if (isAll) {
      if (!isSelectedAll.value) {
        newModel = value
      } else {
        newModel = []
      }
    } else {
      newModel.push(value)
    }
    model.value = newModel
  }

  const isEqual = (a: any, b: any) => {
    return JSON.stringify(a) === JSON.stringify(b)
  }
</script>

<style lang="scss" scoped>
  .custom-ui-checkbox-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    input {
      display: none;
    }
    .custom-ui-checkbox {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1rem;
      height: 1rem;
      border: 1px solid #ccc;
      border-radius: 0.25rem;
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      background-color: #fff;
      &.checked {
        background-color: $brandColor;
        border: 1px solid $brandColor;
      }
      &.disabled {
        cursor: not-allowed;
      }
      .fa-solid {
        color: #fff;
        font-size: 0.75rem;
      }
    }
    .custom-ui-checkbox-label {
      @include row-label;
      &.disabled {
        color: $disabled;
        cursor: not-allowed;
      }
    }
  }
</style>
